import type { CombinedError } from 'urql';

import type { ErrorType } from './kibana-log';

const NETWORK_ABORTED = '[Network] The user aborted a request.';
const NO_MANIFEST = '[GraphQL] sending request: status code not valid: 404';
const FORBIDDEN = '[Network] Forbidden';
const USER_NOT_AUTHORIZED = '[GraphQL] user not authorized for this operation';
const USER_IS_NOT_AUTHORIZED =
  '[GraphQL] user is not authorized for this operation';
const DUPLICATE_USER_ADD = '[GraphQL] rpc error: code = AlreadyExists desc = ';
export const ENTITY_NOT_FOUND = 'entity not found';
export const DELIVERABLES_NAME_NOT_UNIQUE =
  'error storing deliverable: error storing delivery: deliverable name should be unique';
export const DELIVERABLES_REPORT_EXISTS =
  '[GraphQL] error storing deliverable: error storing delivery: report exists';
const SEAT_LIMIT_REACHED = 'seat limit has been reached';

/** Composition / snapshot API rejects invalid skill filter depth; retrying the same request never helps. */
const COMPOSITION_SKILL_FILTER_MAX_LEVEL = 'max level allowed in filters';

/** The API's input validation rejected the request as malformed (e.g. `company_info_selectors` on a non-company screener dim); the same request can never succeed. */
const INVALID_INPUT = 'validating input:';

const TOO_MANY_ENTITIES_ERROR_MESSAGE = 'too many entities specified';

export const isTooManyEntitiesError = (error: Error | string): boolean => {
  const message = typeof error === 'string' ? error : error.message;
  return message.includes(TOO_MANY_ENTITIES_ERROR_MESSAGE);
};

const nonRetriableErrors = [
  NETWORK_ABORTED,
  FORBIDDEN,
  USER_NOT_AUTHORIZED,
  USER_IS_NOT_AUTHORIZED,
  NO_MANIFEST,
  DUPLICATE_USER_ADD,
  ENTITY_NOT_FOUND,
  DELIVERABLES_NAME_NOT_UNIQUE,
  SEAT_LIMIT_REACHED,
  DELIVERABLES_REPORT_EXISTS,
  COMPOSITION_SKILL_FILTER_MAX_LEVEL,
  INVALID_INPUT,
];

export const isNonRetriableError = (error: Error) =>
  isTooManyEntitiesError(error) ||
  nonRetriableErrors.some((nonRetriableError) =>
    error.message.includes(nonRetriableError)
  );

const warningErrors = [
  NO_MANIFEST,
  DUPLICATE_USER_ADD,
  ENTITY_NOT_FOUND,
  DELIVERABLES_NAME_NOT_UNIQUE,
  SEAT_LIMIT_REACHED,
  DELIVERABLES_REPORT_EXISTS,
];

export const isWarningError = (error: CombinedError) => {
  const errorIsAuthError = [401, 403].includes(error.response?.status);
  const errorMessageIsWarning = warningErrors.some((warningError) =>
    error.message.includes(warningError)
  );

  return errorIsAuthError || errorMessageIsWarning;
};

/** ================================
 * Backend specific error codes
 ================================ */
// gRPC statuses the server owns; request-caused ones (InvalidArgument, Unknown...) stay FE.
// Keep in sync with docs/support/error-analytics-dashboard.md and the
// investigate-support-issue skill.
const SERVER_FAULT_GRPC_CODES: ReadonlySet<string> = new Set([
  'DeadlineExceeded',
  'Internal',
  'NotFound',
  'ResourceExhausted',
  'Unavailable',
]);
const GRPC_STATUS_CODE_RE = /\brpc error: code = (\w+)/;

export const getGrpcStatusCode = (message: string): string | undefined =>
  GRPC_STATUS_CODE_RE.exec(message)?.[1];

const NETWORK_FAILED_TO_FETCH = '[Network] Failed to fetch';

export const getUrqlErrorType = (error: CombinedError): ErrorType => {
  const status = error.response?.status;
  const isServerError = status !== undefined && status >= 500;
  const grpcCode = getGrpcStatusCode(error.message);
  const isServerFaultGrpcCode =
    grpcCode !== undefined && SERVER_FAULT_GRPC_CODES.has(grpcCode);
  const isNetworkFailure = error.message.includes(NETWORK_FAILED_TO_FETCH);
  return isServerError || isServerFaultGrpcCode || isNetworkFailure
    ? 'BE'
    : 'FE';
};
